/**
 * React Query hooks for the pre-use template register (api#1209). One query key
 * family, `['preuse-templates', …]`, so any write invalidates the list, the
 * record and the phone's `for-equipment` read together.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { unwrap, unwrapArray } from '../lib/api-envelope';
import { LOAD_FAILURE_QUERY_OPTIONS } from '../lib/load-failure';
import type { PreuseItemWrite, PreuseStarter, PreuseTemplate, PreuseTemplateDetail } from '../types/preuse';
import { toEquipmentType, type EquipmentType } from '../types/equipment-type';

export const PREUSE_KEY = ['preuse-templates'] as const;

export function usePreuseTemplates(params: { status?: string; equipment_type_id?: string } = {}, enabled = true) {
  return useQuery({
    queryKey: [...PREUSE_KEY, 'list', params.status ?? 'all', params.equipment_type_id ?? 'any'],
    queryFn: async () => unwrapArray<PreuseTemplate>((await api.preuseTemplates.list(
      Object.fromEntries(Object.entries(params).filter(([, v]) => !!v)),
    )).data),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePreuseTemplate(id: string | undefined) {
  return useQuery({
    queryKey: [...PREUSE_KEY, 'detail', id],
    queryFn: async () => unwrap<PreuseTemplateDetail>((await api.preuseTemplates.get(id!)).data),
    enabled: !!id,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

export function usePreuseStarters() {
  return useQuery({
    queryKey: [...PREUSE_KEY, 'starters'],
    queryFn: async () => unwrapArray<PreuseStarter>((await api.preuseTemplates.starters()).data),
    staleTime: 60 * 60 * 1000,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** The tenant's ACTIVE equipment types — the API refuses a template on an inactive one. */
export function useActiveEquipmentTypes(enabled = true) {
  return useQuery({
    // Its own key: other screens cache ['equipment-types'] in their own shapes.
    queryKey: ['equipment-types', 'active', 'preuse'],
    queryFn: async () => unwrapArray<any>((await api.equipmentTypes.list()).data).map(toEquipmentType).filter((t): t is EquipmentType => t !== null && t.isActive !== false),
    enabled,
    ...LOAD_FAILURE_QUERY_OPTIONS,
  });
}

/** Display name of the user who published — the template carries only an id. */
export function useUserDisplayName(userId: string | null | undefined) {
  return useQuery({
    queryKey: ['user-display-name', userId],
    queryFn: async () => {
      const u = unwrap<{ firstName?: string; lastName?: string; first_name?: string; last_name?: string }>((await api.users.get(userId!)).data);
      const name = [u?.firstName ?? u?.first_name, u?.lastName ?? u?.last_name].filter(Boolean).join(' ').trim();
      return name || null;
    },
    enabled: !!userId,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: PREUSE_KEY });
    qc.invalidateQueries({ queryKey: ['preuse-for-equipment'] });
  };
}

export function usePreuseMutations() {
  const invalidate = useInvalidate();
  const opts = { onSuccess: invalidate };
  return {
    create: useMutation({
      mutationFn: async (body: Parameters<typeof api.preuseTemplates.create>[0]) => unwrap<PreuseTemplateDetail>((await api.preuseTemplates.create(body)).data),
      ...opts,
    }),
    fromStarter: useMutation({
      mutationFn: async (body: Parameters<typeof api.preuseTemplates.fromStarter>[0]) => unwrap<PreuseTemplateDetail>((await api.preuseTemplates.fromStarter(body)).data),
      ...opts,
    }),
    /** PATCH the header fields (if any) then PUT the whole item list — the editor's one Save. */
    save: useMutation({
      mutationFn: async (v: { id: string; header: Parameters<typeof api.preuseTemplates.update>[1] | null; items: PreuseItemWrite[] }) => {
        if (v.header && Object.keys(v.header).length > 0) await api.preuseTemplates.update(v.id, v.header);
        return unwrap<PreuseTemplateDetail>((await api.preuseTemplates.replaceItems(v.id, v.items)).data);
      },
      ...opts,
    }),
    duplicate: useMutation({
      mutationFn: async (id: string) => unwrap<PreuseTemplateDetail>((await api.preuseTemplates.duplicate(id)).data),
      ...opts,
    }),
    publish: useMutation({
      mutationFn: async (id: string) => unwrap<PreuseTemplateDetail>((await api.preuseTemplates.publish(id)).data),
      ...opts,
    }),
    archive: useMutation({
      mutationFn: async (id: string) => unwrap<PreuseTemplateDetail>((await api.preuseTemplates.archive(id)).data),
      ...opts,
    }),
    remove: useMutation({
      mutationFn: async (id: string) => (await api.preuseTemplates.remove(id)).data,
      ...opts,
    }),
  };
}
